import React, { useState, useEffect, useCallback } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { membershipDetailsQuery } from "../../queries/membershipDetailsQuery";
import BecomeMemberShip from "components/BecomeMemberShip";
import { MembershipDetailsList }  from "components/MembershipDetails";
import { ModalMembershipValidation } from "components/ModalMembershipValidation/ModalMembershipValidation";
import { SpinnerBack } from "components/Spinner/Spinner";
import { CLUB_IDS }  from "components/MembershipDetails/constants";
import classes from "./MembershipDetails.module.css";
import {
  extractSuccess,
  extractServerErrorMessage
} from "utils/extractErrors";
import { userPhoneVerifyQuery } from "components/MembershipValidationModal/queries";
import { membershipValidate } from "components/MembershipValidationModal/mutations";
import NotificationPopover from "components/NotificationPopover/NotificationPopover";
import { MembershipValidationModal } from "components/MembershipValidationModal";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { MEMBERSHIP_VALIDATION_MODES } from "constants/membershipValidationModes";

const extractMembershipValidateSuccess = extractSuccess("membership_validate");
const extractMembershipValidateErrorMessage = extractServerErrorMessage("membership_validate");

export const MembershipDetails = ({ data: membershipDetails, onRefetch }) => {
  const { isFeatureFlagEnabled: isAgisMembershipMode } = useFeatureFlags(MEMBERSHIP_VALIDATION_MODES.AGIS);
  const [isShowBecomeMemberShip, setShowBecomeMemberShip] = useState(false);
  const [isShowModalMembershipValidation, setIsShowModalMembershipValidation] = useState(false);
  const [isMembershipValidationSuccess, setIsMembershipValidationSuccess] = useState(false);
  const [error, setError] = useState("");
  const [
    load,
    { data: { membership_details = [] } = {}, loading }
  ] = useLazyQuery(membershipDetailsQuery,{
    fetchPolicy: "no-cache"
  });

  const [membershipValidateAction, { loading: membershipValidateLoading }] = useMutation(membershipValidate, {
    fetchPolicy: "no-cache",
    onCompleted: (response) => {
      if(extractMembershipValidateSuccess({ data: response })) {
        setError("");
        setIsMembershipValidationSuccess(true);
        setIsShowModalMembershipValidation(false);
        if (onRefetch) onRefetch();
        else {
          membershipDetailsLoad();
        }
      } else {
        const errorText = extractMembershipValidateErrorMessage({ data: response });
        setError(errorText);
      }
    }
  });

  const [userPhoneVerifyAction, { loading: userPhoneVerifyLoading }] = useLazyQuery(userPhoneVerifyQuery, {
    fetchPolicy: "no-cache",
    onCompleted: (response) => {
      if (response?.user?.phone_verified) {
        membershipValidateAction({
          variables: {
            club_id: CLUB_IDS.GOODSAM,
            phone: response?.user?.phone
          }
        });
      } else {
        setIsShowModalMembershipValidation(true);
      }
    }
  });
  const membershipDetailsData = membershipDetails || membership_details;

  const membershipDetailsLoad = useCallback(() => {
    load();
  }, [load]);

  const onMembershipValidationSuccess = useCallback(() => {
    setError("");
    setIsMembershipValidationSuccess(true);
    setIsShowModalMembershipValidation(false);
    if (onRefetch) onRefetch();
    else {
      membershipDetailsLoad();
    }
  }, [membershipDetailsLoad, onRefetch, setIsShowModalMembershipValidation]);

  const showModalMembershipValidation = useCallback(() => {
    userPhoneVerifyAction();
  }, [userPhoneVerifyAction]);

  const onModalMembershipValidation = useCallback(() => {
    setIsShowModalMembershipValidation(false);
  }, []);

  const showBecomeMemberShip = useCallback(() => {
    setShowBecomeMemberShip(true);
  }, []);

  const onCloseBecomeMemberShip = useCallback(() => {
    setShowBecomeMemberShip(false);
  }, []);

  useEffect(() => {
    if (!membershipDetails) {
      membershipDetailsLoad();
    }
  }, [membershipDetails, membershipDetailsLoad]);

  return (
    <div className={classes.container}>
      <MembershipDetailsList
        data={membershipDetailsData}
        links={{
          [CLUB_IDS.GOODSAM]: {
            joinLink: showBecomeMemberShip,
            memberLink: showModalMembershipValidation
          }
        }}
      />
      <BecomeMemberShip
        show={isShowBecomeMemberShip}
        onCloseBecomeMemberShip={onCloseBecomeMemberShip}
        handleActionAfterLastStep={onRefetch || membershipDetailsLoad}
        setIsShowMembershipModal={() => {
          if (!isShowBecomeMemberShip) setShowBecomeMemberShip(true);
        }}
        onMembershipValidationSuccess={onRefetch || membershipDetailsLoad}
      />

      {isShowModalMembershipValidation && !isAgisMembershipMode ? (
        <ModalMembershipValidation
          clubId={CLUB_IDS.GOODSAM}
          show={isShowModalMembershipValidation}
          onClose={onModalMembershipValidation}
          onMembershipValidationSuccess={onMembershipValidationSuccess}
        />
      ): null}

      {isShowModalMembershipValidation && isAgisMembershipMode ? (
        <MembershipValidationModal
          show={isShowModalMembershipValidation}
          clubId={CLUB_IDS.GOODSAM}
          onClose={onModalMembershipValidation}
          onVerified={onMembershipValidationSuccess}
        />
      ) : null}

      {isMembershipValidationSuccess && (
        <NotificationPopover
          show={true}
          status="success"
          text="You successfully validated your membership details"
          onClose={() => {
            setIsMembershipValidationSuccess(false);
          }}
        />
      )}

      {error?.length !== 0 && (
        <NotificationPopover
          show={true}
          status="error"
          text={error}
          onClose={() => {
            setError('');
          }}
        />
      )}
      {(loading || userPhoneVerifyLoading || membershipValidateLoading) && <SpinnerBack />}
    </div>
  );
};

MembershipDetails.defaultProps = {

};

MembershipDetails.propTypes = {

};
